import React from 'react';
import { useTranslation } from 'react-i18next';
import { ui } from '../ui';

export type Scope = 'missing' | 'ready' | 'done';

interface Props {
  blocked: number;
  missing: number;
  ready: number;
  done: number;
  scope: Scope | null;
  /** Songwriter ID not set up: the three bucket tiles show a dash and cannot be selected. */
  blockedIdentity: boolean;
  onChange: (scope: Scope | null) => void;
}

/** Clickable stat tiles above the track lists; a selected tile narrows the lists to that bucket. */
export function ScopeTiles({
  blocked,
  missing,
  ready,
  done,
  scope,
  blockedIdentity,
  onChange,
}: Props) {
  const { t } = useTranslation('publishing');
  const tiles: { key: Scope | null; label: string; count: number; color: string }[] = [
    {
      key: null,
      label: t('list_all'),
      count: blocked + missing + ready + done,
      color: 'text-[#222222]',
    },
    { key: 'missing', label: t('tile_need_attention'), count: missing, color: 'text-amber-700' },
    { key: 'ready', label: t('tile_look_fine'), count: ready, color: 'text-[#222222]' },
    { key: 'done', label: t('list_done'), count: done, color: 'text-[#8C8C8C]' },
  ];
  return (
    <div className="mt-4 flex flex-wrap gap-3">
      {tiles.map((x) => {
        const disabled = blockedIdentity && x.key !== null;
        const active = !disabled && scope === x.key;
        return (
          <button
            key={x.key || 'all'}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(x.key)}
            className={`box-border min-w-[200px] flex-1 rounded-xl border bg-white px-5 py-4 text-left ${
              disabled ? 'cursor-default' : 'hover:bg-gray-50'
            } ${active ? 'border-[#7801ef] ring-1 ring-[#7801ef]' : 'border-gray-200'}`}
          >
            <div className={ui.muted}>{x.label}</div>
            <div className={`mt-1 text-3xl ${disabled ? 'text-[#8C8C8C]' : x.color}`}>
              {disabled ? '–' : x.count}
            </div>
          </button>
        );
      })}
    </div>
  );
}
